import type { PublicShareFolder } from '@signa/shared';
import {
  fetchFolder,
  VIEWER_ERROR_CODES,
  ViewerError,
  type ViewerErrorCode,
} from '../shared/api.js';
import { errorMessage, isRetryable, messages } from '../shared/messages.js';
import { ViewerShell } from '../shared/shell.js';
import { sendFolderView } from '../shared/view-beacon.js';
import { folderStyles } from './folder-styles.js';
import { folderViewTarget, hashForTab, nextTab, tabFromHash } from './folder-tabs.js';
import { createLogo, shouldShowLogo } from './logo.js';

/**
 * 共有フォルダのページ(V02 —— #267)。
 *
 * 共有フォルダのリンク(`?token=pf_…`)で開くと、**フォルダ名と 1 段のタブ**を出し、
 * タブで盤面を切り替える。正本は [#33](../../../../docs/issues/33-viewer-embed/DESIGN.md) の
 * 「V02 の共有フォルダ」。
 *
 * - **盤面の表示はシェル(`ViewerShell`)に任せる。** ここは外枠(見出し・タブ・状態の表示)だけ
 * - **WebGL の描画は同時に 1 つ。** 切り替えのたびに前のシェルを捨ててから次を作る
 *   (同意書)。**閲覧者が入れた素材も一緒に外れる**(同意書 Q1)
 * - **フォルダは最初に 1 回だけ取る**(全タブの中身が入っている —— ADR-0046)。切り替えで公開 API を叩かない
 * - **名前はテキストとしてのみ描く**(`textContent` / `title`。ADR-0046 の決定 3)
 * - **選んだタブは `#tab=N` に残す**(同意書 Q3)。履歴は積まない(`replaceState`)
 * - **表示した盤面ごとに閲覧を通知する**(#290 / [ADR-0051](../../../../docs/adr/0051-folder-view-count.md))。
 *   タブのシェルが `ready`(表示が終わった)を出したときに 1 回。30 分判定はタブごと(`sendFolderView`)
 */

const PANEL_ID = 'signa-folder-panel';
const TITLE_ID = 'signa-folder-title';

/** ブラウザのタブのタイトルの後ろ。**`index.html` の `<title>` と同じ書き方。** */
const TITLE_SUFFIX = ' | SIGNA Preview 2D';

export interface FolderPageOptions {
  /** 共有フォルダのトークン(`pf_`)。**形は入口で確かめてある**(`tokenFromLocation`)。 */
  readonly token: string;
  readonly apiBase: string;
}

/** タブの選び方。 */
interface SelectHow {
  /** 選んだタブにフォーカスを移す(キー操作のとき)。 */
  readonly focus: boolean;
  /** URL の `#tab=N` を書き換える(利用者が選んだとき。最初の表示と `hashchange` では書かない)。 */
  readonly updateHash: boolean;
}

export class FolderPage {
  private readonly root: HTMLDivElement;
  private readonly header: HTMLElement;
  private readonly title: HTMLHeadingElement;
  private readonly tablist: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly status: HTMLDivElement;
  private tabs: HTMLButtonElement[] = [];
  private folder: PublicShareFolder | null = null;
  /** 選んでいるタブ(0 始まり)。**まだ選んでいなければ -1。** */
  private current = -1;
  private shell: ViewerShell | null = null;
  /** 動画の書き出し中(タブを押せない)。シェルの `onBusyChange` が知らせる。 */
  private busy = false;
  private destroyed = false;
  private headerObserver: ResizeObserver | null = null;
  /** ロゴとフォルダ名の行(#318)。**ロゴを出すフォルダだけ**作る。 */
  private heading: HTMLDivElement | null = null;

  constructor(
    container: HTMLElement,
    private readonly options: FolderPageOptions,
  ) {
    const style = document.createElement('style');
    style.textContent = folderStyles();

    this.root = el('div', 'signa-folder');

    // **見出しはフォルダを読むまで出さない**(名前がまだ分からない)。
    this.header = el('header', 'signa-folder-header');
    this.header.hidden = true;
    this.title = el('h1', 'signa-folder-title');
    this.title.id = TITLE_ID;
    this.tablist = el('div', 'signa-folder-tabs');
    this.tablist.setAttribute('role', 'tablist');
    this.tablist.setAttribute('aria-labelledby', TITLE_ID);
    this.tablist.hidden = true;
    this.tablist.addEventListener('keydown', this.onKeydown);
    this.header.append(this.title, this.tablist);

    this.panel = el('div', 'signa-folder-panel');
    this.panel.id = PANEL_ID;
    this.panel.setAttribute('role', 'tabpanel');
    /*
     * **盤面の領域にキーボードで入れるようにする**(WAI-ARIA のタブの作法)。素材を入れるまで、
     * 盤面の中にフォーカスできる要素が無い(操作のボタンは隠れている)。
     */
    this.panel.tabIndex = 0;
    this.panel.hidden = true;

    this.status = el('div', 'signa-folder-status');
    this.status.hidden = true;

    /*
     * **シェルの外(見出し・タブの帯・余白)にファイルを落としても、ページから離れない**ようにする。
     * 何もしないとブラウザがファイルを開く。**シェルの中はシェルが受ける**(`preventDefault` 済み)
     * ので触らない。
     */
    this.root.addEventListener('dragover', this.onOutsideDrag);
    this.root.addEventListener('drop', this.onOutsideDrag);

    this.root.append(style, this.header, this.panel, this.status);
    container.append(this.root);
  }

  /** フォルダを取り、見出しとタブを出して、URL の `#tab=N` のタブを開く。 */
  async start(): Promise<void> {
    this.showStatus(messages().loading, false);
    let folder: PublicShareFolder;
    try {
      folder = await fetchFolder(this.options.apiBase, this.options.token);
    } catch (error) {
      if (this.destroyed) return;
      this.fail(error instanceof ViewerError ? error.code : VIEWER_ERROR_CODES.NETWORK);
      return;
    }
    if (this.destroyed) return;
    try {
      this.render(folder);
    } catch {
      // **応答の形が崩れていても「読み込み中…」のまま止めない。** 再試行で取り直せる。
      this.fail(VIEWER_ERROR_CODES.NETWORK);
    }
  }

  /** シェルと監視を外す。**ページを離れるときに呼ぶ**(`pagehide`)。 */
  destroy(): void {
    this.destroyed = true;
    this.shell?.destroy();
    this.shell = null;
    this.headerObserver?.disconnect();
    this.headerObserver = null;
    globalThis.removeEventListener('hashchange', this.onHashChange);
    this.root.remove();
  }

  private render(folder: PublicShareFolder): void {
    this.folder = folder;

    // **名前はテキストとしてのみ描く**(ADR-0046 の決定 3)。全文はマウスを載せると出る。
    document.title = `${folder.name}${TITLE_SUFFIX}`;
    this.title.textContent = folder.name;
    this.title.title = folder.name;
    if (shouldShowLogo(folder)) this.showLogo();
    this.header.hidden = false;

    if (folder.tabs.length === 0) {
      // **0 件でもリンクは生きている**(ADR-0046 の決定 4)。フォルダ名と一緒に伝える。
      this.showStatus(messages().emptyFolder, true);
      return;
    }
    this.hideStatus();

    this.tabs = folder.tabs.map((data, index) => {
      const tab = el('button', 'signa-folder-tab');
      tab.type = 'button';
      tab.id = `signa-folder-tab-${String(index)}`;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('aria-controls', PANEL_ID);
      tab.tabIndex = -1;
      tab.textContent = data.name;
      tab.title = data.name;
      tab.addEventListener('click', () => {
        this.select(index, { focus: false, updateHash: true });
      });
      return tab;
    });
    this.tablist.replaceChildren(...this.tabs);
    this.tablist.hidden = false;
    this.panel.hidden = false;

    /*
     * **見出しとタブの帯の分を、盤面の高さの上限から差し引く**(#212 の「画面に収める」)。
     * 名前の長さや文字の大きさで帯の高さが変わるので、測り続ける。
     */
    this.headerObserver = new ResizeObserver(() => {
      this.shell?.setViewportOffset(this.headerHeight());
    });
    this.headerObserver.observe(this.header);
    globalThis.addEventListener('hashchange', this.onHashChange);

    this.select(tabFromHash(globalThis.location.hash, this.tabs.length), {
      focus: false,
      updateHash: false,
    });
  }

  /**
   * タブを選ぶ。**書き出し中は何もしない**(タブも押せなくしてある)。
   *
   * **前のシェルを捨ててから次を作る**(WebGL は同時に 1 つ)。捨てると閲覧者が入れた
   * 素材も外れる(同意書 Q1)。同じタブを選び直したときは作り直さない。
   */
  private select(index: number, how: SelectHow): void {
    const folder = this.folder;
    if (folder === null || this.busy) return;
    const tab = this.tabs[index];
    const data = folder.tabs[index];
    if (tab === undefined || data === undefined) return;

    if (index !== this.current) {
      this.shell?.destroy();
      this.shell = null;
      this.current = index;
      for (const [i, other] of this.tabs.entries()) {
        const selected = i === index;
        other.setAttribute('aria-selected', String(selected));
        // **選んだタブだけがフォーカスの順に入る**(残りは左右のキーで移る)。
        other.tabIndex = selected ? 0 : -1;
      }
      this.panel.setAttribute('aria-labelledby', tab.id);

      const shell: ViewerShell = new ViewerShell(this.panel, {
        template: data.template,
        apiBase: this.options.apiBase,
        fit: 'contain',
        fitViewport: true,
        // **いまのシェルの知らせだけを聞く**(捨てたシェルが後から知らせても、タブを止めない)。
        onBusyChange: (busy) => {
          if (this.shell === shell) this.setBusy(busy);
        },
        /*
         * **表示が終わったら、このタブの盤面の閲覧を通知する**(#290 / ADR-0051 の決定 7)。
         * 盤面の単体ページと同じく「表示できたときだけ」(`ready`)。**捨てたシェルの知らせでは
         * 送らない**(表示が終わる前に別のタブへ移った盤面は数えない)。タブに `id` が無い
         * 古い応答(キャッシュ)でも送らない。
         */
        onEvent: (event) => {
          const tabId = folderViewTarget(event.type, this.shell === shell, data);
          if (tabId !== null) sendFolderView(this.options.apiBase, this.options.token, tabId);
        },
      });
      this.shell = shell;
      shell.setViewportOffset(this.headerHeight());
      void shell.start();
    }

    if (how.updateHash) {
      // **履歴は積まない。** 戻るボタンでタブを行き来させない(ページを離れる操作のまま)。
      globalThis.history.replaceState(null, '', hashForTab(index));
    }
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (how.focus) tab.focus();
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    for (const tab of this.tabs) tab.disabled = busy;
  }

  /**
   * **見出しの行の右端にロゴを置く**(右上 —— フォルダ名は左のまま)(#318 / [ADR-0053](../../../../docs/adr/0053-share-link-logo.md))。
   *
   * ロゴとフォルダ名を 1 行にまとめる。**ロゴを出さないフォルダでは行を作らない**(見出しは今のまま)。
   * 見出しの高さは既存の `ResizeObserver` が測って、盤面の高さの上限から差し引く。
   */
  private showLogo(): void {
    if (this.heading !== null) return;
    const heading = el('div', 'signa-folder-heading');
    this.title.before(heading);
    heading.append(this.title, createLogo('signa-folder-logo'));
    this.heading = heading;
  }

  /**
   * 見出しとタブの帯の高さ(px)。**切り上げる** —— `offsetHeight` は整数に丸めた値で、
   * 端数(`16px × 1.4` など)を切り捨てると差し引きが足りず、1px はみ出すことがある。
   */
  private headerHeight(): number {
    return Math.ceil(this.header.getBoundingClientRect().height);
  }

  /** タブの帯のキー操作(左右・Home・End —— WAI-ARIA のタブの作法)。 */
  private readonly onKeydown = (event: KeyboardEvent): void => {
    // **修飾キー付きは横取りしない**(Alt+← の「戻る」など、ブラウザの操作とぶつかる)。
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const next = nextTab(this.current, event.key, this.tabs.length);
    if (next === null) return;
    event.preventDefault();
    this.select(next, { focus: true, updateHash: true });
  };

  /**
   * URL の `#tab=N` を手で書き換えたとき(`replaceState` では起きない)。
   *
   * **書き出し中は切り替えず、URL を今のタブに戻す**(表示と URL を食い違わせない)。
   */
  private readonly onHashChange = (): void => {
    if (this.busy) {
      globalThis.history.replaceState(null, '', hashForTab(this.current));
      return;
    }
    this.select(tabFromHash(globalThis.location.hash, this.tabs.length), {
      focus: false,
      updateHash: false,
    });
  };

  /**
   * シェルの外へのドラッグとドロップ。**誰も止めていなければ止める**(ページから離れない)。
   *
   * シェルの中の操作はシェルが先に `preventDefault` している(`bindDragAndDrop`)ので、
   * `defaultPrevented` で見分けて触らない —— 触ると、盤面へのドロップのカーソルを上書きしてしまう。
   */
  private readonly onOutsideDrag = (event: DragEvent): void => {
    if (event.defaultPrevented) return;
    event.preventDefault();
    // カーソルは「置けない」にする(落としても何も起きない場所なので)。
    if (event.type === 'dragover' && event.dataTransfer !== null) {
      event.dataTransfer.dropEffect = 'none';
    }
  };

  private fail(code: ViewerErrorCode): void {
    const m = messages();
    // 入口で形を確かめているので「何も出さない」コードは来ないはずだが、来ても黙らない。
    this.showStatus(errorMessage(code) ?? m.notAvailable, true);
    if (isRetryable(code)) {
      const retry = el('button', 'signa-folder-retry');
      retry.type = 'button';
      retry.textContent = m.retry;
      retry.addEventListener('click', () => {
        void this.start();
      });
      this.status.append(retry);
    }
  }

  private showStatus(text: string, emphasis: boolean): void {
    this.status.hidden = false;
    this.status.classList.toggle('signa-folder-status-emphasis', emphasis);
    this.status.replaceChildren(document.createTextNode(text));
  }

  private hideStatus(): void {
    this.status.hidden = true;
    this.status.replaceChildren();
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  element.className = className;
  return element;
}
